import type { Form } from '@facile-it/shark-morph/components'
import * as O from 'fp-ts/Option'
import { pipe } from 'fp-ts/function'
import * as React from 'react'
import type { Subscription } from 'rxjs'
import { Subject } from 'rxjs'
import { withLatestFrom } from 'rxjs/operators'
import { evaluateFieldsToExlude, evaluateFieldsToReset, findFieldAppears } from './FormEvaluetor'
import { buildFormStatus, buildInitialFieldValueFromUpdater, buildInitialInternalValueFields } from './FormInitializer'
import { fieldsMerger, getFieldsToReset } from './FormSlave'
import type { Unit } from './Unit'
import type { Employe } from './employees/Employe'
import { EmployeSubscriber } from './employees/EmployeSubscriber'
import { employeesStatusReducer } from './employees/employeesStatusReducer'
import { runEmployees } from './employees/runEmployees'
import type {
    employeeStatus,
    FieldsState,
    FieldsValue,
    FieldValidator,
    FieldValue,
    formResource,
    FormStatus,
    helper,
    Structure,
    submit,
    updateSubject,
    UseType,
} from './formBuilder'
import { FromRenderer } from './unitsRenderer/FormRenderer'
import type { Validator } from './validators/Validator'
import { ValidatorSubscriber } from './validators/ValidatorSubscriber'
import { fieldValidatorsReducer, fieldValidatorsToUnknown } from './validators/fieldValidatorsReducer'
import type { runType } from './validators/runValidators'
import { mapFieldsToRunType, runAllValidators, unscribeSubscription } from './validators/runValidators'

export namespace FormWorker {
    export interface Props<
        unitsFormT = { [k: string]: Unit.FormUnit<Unit.UnionFields['c'], string> },
        employeesT = {
            [k: string]: Employe.EmployeBuilded<InternalStateFields, keyof unitsFormT, any, any>
        },
        validatorT = {
            [k: string]: Validator.validatorMultipleBuilded<FieldsValue, keyof unitsFormT>
        },
    > {
        name: string
        formStyle?: Form.StyleProps
        unitsForm: unitsFormT
        unitsHtml: (fields: InternalStateFields) => { [k: string]: Unit.HtmlUnit }
        structure: (es: EmployeesStatus, fields: InternalStateFields) => Structure<string>
        onSubmit?: (form: FormStatus<string>, fields: InternalStateFields) => void
        onChange?: (
            form: FormStatus<string>,
            fields: InternalStateFields,
            name: string,
            unitsForm: unitsFormT,
            subject: Subject<any>
        ) => void
        resetOnChange?: { observed: string; list: Array<string> }[]
        employees?: {
            config: employeesT
            slave: { [k in keyof employeesT]: employeSlave }
        }
        helper?: (es: EmployeesStatus, fields: InternalStateFields) => helper<string>
        validators?: validatorT
        fieldsUseMarker?: (
            c: InternalValueFields,
            initialized: { [k: string]: UseType },
            es: EmployeesStatus
        ) => typeof initialized
        tagsUseMarker?: (
            fields: InternalValueFields,
            initialized: { [k: string]: UseType },
            es: EmployeesStatus
        ) => typeof initialized
        submit: submit
        resource?: formResource<unknown, unknown, boolean>
    }

    export interface updateSubjects {
        runType: EmployeSubscriber.runType
        fields: { [k: string]: updateSubject<Unit.UnionFields['c']> }
    }

    export interface employeSlave {
        onNext: (
            runType: EmployeSubscriber.runType,
            next: any,
            subject: Subject<any>,
            unitsForm: FormWorker.Props['unitsForm'],
            fieldsMerged: FieldsState<string>
        ) => void
        onError?: (
            runType: EmployeSubscriber.runType,
            error: any,
            subject: Subject<any>,
            unitsForm: FormWorker.Props['unitsForm'],
            fieldsMerged: FieldsState<string>
        ) => void
    }

    export type formUnitNamized = Unit.FormUnit<Unit.UnionFields['c'], string> & { name: string }

    export type ActionValidator =
        | { type: 'starting'; payload: { name: string; fieldsToReset: string[] } }
        | { type: 'completed'; payload: InternalValidatorField }

    export type dispatchValidatorsResult = React.Dispatch<
        React.ReducerAction<React.Reducer<InternalValidatorFields, ActionValidator>>
    >

    export interface EmployeesStatus {
        [k: string]: employeeStatus
    }

    export interface ActionEmployeesStatus {
        type: employeeStatus
        name: string
    }

    export type dispatchEmployeesStatus = React.Dispatch<
        React.ReducerAction<React.Reducer<EmployeesStatus, ActionEmployeesStatus>>
    >

    export type employeesStatusOnNext = (action: FormWorker.ActionEmployeesStatus) => void

    export type InternalStateFields = FieldsState<string>
    export type StateFieldsUnion = InternalValueFields | InternalValidatorFields
    export type StateFieldUnion = InternalValueField | InternalValidatorField

    export type InternalValidatorField<name extends string = string> = FieldValidator<name>

    export interface InternalValidatorFields {
        [k: string]: InternalValidatorField
    }

    export type InternalValueField<componentType extends Unit.UnionFields['c'] = Unit.UnionFields['c']> = FieldValue<
        string,
        componentType
    > & {
        tags: string[]
        observerEmployees: string[]
    }

    export interface InternalValueFields {
        [k: string]: InternalValueField
    }

    export type ValueForRunValidators = Array<InternalValueField & { runType: runType }>

    export interface Subscriptions {
        validatorSubscriber: ValidatorSubscriber
        employeSubscriber: EmployeSubscriber
        allValidatorsSubscription: O.Option<Subscription>
        updaterSubcrition: {
            subject: Subject<updateSubjects>
            subscription: O.Option<Subscription>
        }
        fieldsSubcritionsSubject: Subject<InternalValueFields>
        employeesStatusSubcritionsSubject: Subject<FormWorker.EmployeesStatus>
    }
}

export const FormWorker: React.FunctionComponent<FormWorker.Props> = (props: FormWorker.Props) => {
    const [fieldValidators, dispatchValidators] = React.useReducer(
        fieldValidatorsReducer,
        {} as FormWorker.InternalValidatorFields
    )

    const [employeesStatus, dispatchEmployeesStatus] = React.useReducer(
        employeesStatusReducer,
        {} as FormWorker.EmployeesStatus,
        _arg =>
            pipe(
                O.fromNullable(props.employees),
                O.map(e =>
                    Object.keys(e.config).reduce(
                        (c: FormWorker.EmployeesStatus, k): FormWorker.EmployeesStatus => ({ ...c, [k]: 'init' }),
                        {}
                    )
                ),
                O.getOrElse(() => ({}))
            )
    )
    const [fields, setFields] = React.useState(() =>
        evaluateFieldsToExlude(
            buildInitialInternalValueFields(props.unitsForm, props.employees),
            props.fieldsUseMarker,
            props.tagsUseMarker,
            employeesStatus
        )
    )

    const [subscriptions, setSubscriptions] = React.useState<FormWorker.Subscriptions>(() => ({
        validatorSubscriber: new ValidatorSubscriber(),
        employeSubscriber: new EmployeSubscriber(),
        allValidatorsSubscription: O.none,
        updaterSubcrition: {
            subject: new Subject<FormWorker.updateSubjects>(),
            subscription: O.none,
        },
        fieldsSubcritionsSubject: new Subject<FormWorker.InternalValueFields>(),
        employeesStatusSubcritionsSubject: new Subject<FormWorker.EmployeesStatus>(),
    }))

    const employeesOnNextBuilded: FormWorker.employeesStatusOnNext = (action: FormWorker.ActionEmployeesStatus) => {
        if (subscriptions.updaterSubcrition.subject.isStopped) {
            return
        }

        subscriptions.employeesStatusSubcritionsSubject.next(employeesStatusReducer(employeesStatus, action))

        dispatchEmployeesStatus(action)
    }

    React.useEffect(() => {
        const updaterSubscrition = O.some(
            subscriptions.updaterSubcrition.subject
                .pipe(
                    withLatestFrom(
                        subscriptions.fieldsSubcritionsSubject,
                        subscriptions.employeesStatusSubcritionsSubject
                    )
                )
                .subscribe(([valuesChanged, filedFresh, employeesStatusFresh]) => {
                    const nameUnitsChanged = Object.keys(valuesChanged.fields)

                    if (nameUnitsChanged.length === 0) {
                        return
                    }

                    const nFields = evaluateFieldsToExlude(
                        nameUnitsChanged.reduce(
                            (c, k) => ({
                                ...c,
                                [k]: buildInitialFieldValueFromUpdater(
                                    valuesChanged.runType,
                                    filedFresh[k],
                                    valuesChanged.fields[k]
                                ),
                            }),
                            filedFresh
                        ),
                        props.fieldsUseMarker,
                        props.tagsUseMarker,
                        employeesStatusFresh
                    )

                    const filedsNameToValidateToInit = findFieldAppears(filedFresh, nFields)

                    setFields(nFields)
                    subscriptions.fieldsSubcritionsSubject.next(nFields)

                    const nFieldChanged = Object.values(nFields)
                        .filter(f => nameUnitsChanged.includes(f.name) || filedsNameToValidateToInit.includes(f.name))
                        .map((f): FormWorker.ValueForRunValidators[0] => ({
                            ...f,
                            runType: valuesChanged.fields[f.name]?.current?.forceOnInt === true ? 'all' : 'init',
                        }))

                    setSubscriptions({
                        ...subscriptions,
                        allValidatorsSubscription: runAllValidators(
                            subscriptions.allValidatorsSubscription,
                            subscriptions.validatorSubscriber,
                            nFieldChanged,
                            nFields,
                            props.unitsForm,
                            dispatchValidators,
                            props.resetOnChange,
                            props.validators
                        ),
                    })
                })
        )

        subscriptions.fieldsSubcritionsSubject.next(fields)
        subscriptions.employeesStatusSubcritionsSubject.next(employeesStatus)

        const allValidatorsSubscription = runAllValidators(
            subscriptions.allValidatorsSubscription,
            subscriptions.validatorSubscriber,
            mapFieldsToRunType(fields, 'init'),
            fields,
            props.unitsForm,
            dispatchValidators,
            props.resetOnChange,
            props.validators,
            nFieldValidators => {
                const fieldValidated = Object.keys(nFieldValidators)
                runEmployees(
                    props.employees,
                    subscriptions.employeSubscriber,
                    Object.values(fields).filter(f => fieldValidated.includes(f.name)),
                    fieldsMerger(fields, nFieldValidators),
                    props.unitsForm,
                    subscriptions.updaterSubcrition.subject,
                    'init',
                    employeesOnNextBuilded
                )
            }
        )

        setSubscriptions({
            ...subscriptions,
            allValidatorsSubscription: allValidatorsSubscription,
            updaterSubcrition: {
                ...subscriptions.updaterSubcrition,
                subscription: updaterSubscrition,
            },
        })

        return () => {
            subscriptions.validatorSubscriber.unscribeAll()
            subscriptions.employeSubscriber.unscribeAll()
            subscriptions.updaterSubcrition.subject.complete()
            unscribeSubscription(updaterSubscrition)
            unscribeSubscription(subscriptions.updaterSubcrition.subscription)
            unscribeSubscription(allValidatorsSubscription)
            unscribeSubscription(subscriptions.allValidatorsSubscription)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <FromRenderer
            formName={props.name}
            formStyle={props.formStyle}
            onSubmit={e => {
                e.preventDefault()

                if (Object.values(employeesStatus).filter(es => es === 'pending').length !== 0) {
                    return
                }

                setSubscriptions({
                    ...subscriptions,
                    allValidatorsSubscription: runAllValidators(
                        subscriptions.allValidatorsSubscription,
                        subscriptions.validatorSubscriber,
                        mapFieldsToRunType(fields, 'all'),
                        fields,
                        props.unitsForm,
                        dispatchValidators,
                        props.resetOnChange,
                        props.validators,
                        nFieldValidators => {
                            const formStatus = buildFormStatus(props.name, fields, nFieldValidators)
                            if (formStatus.isValid && props.onSubmit) {
                                props.onSubmit(formStatus, fieldsMerger(fields, nFieldValidators))
                            }
                        }
                    ),
                })
            }}
            onChange={name => (value, data) => {
                const nField = { ...fields[name], value: value, data: data }
                const nFields = evaluateFieldsToExlude(
                    {
                        ...evaluateFieldsToReset(props.resetOnChange, { ...fields, [name]: nField }, name),
                        [name]: nField,
                    },
                    props.fieldsUseMarker,
                    props.tagsUseMarker,
                    employeesStatus
                )

                const filedsNameToValidateToInit = findFieldAppears(fields, nFields)

                const nFieldToValidate = Object.values(nFields)
                    .filter(f => f.name === name || filedsNameToValidateToInit.includes(f.name))
                    .map((f): FormWorker.ValueForRunValidators[0] => ({
                        ...f,
                        runType: f.name === name ? 'changed' : 'init',
                    }))

                setFields(nFields)
                subscriptions.fieldsSubcritionsSubject.next(nFields)

                setSubscriptions({
                    ...subscriptions,
                    allValidatorsSubscription: runAllValidators(
                        subscriptions.allValidatorsSubscription,
                        subscriptions.validatorSubscriber,
                        nFieldToValidate,
                        nFields,
                        props.unitsForm,
                        dispatchValidators,
                        props.resetOnChange,
                        props.validators,
                        nFieldValidators => {
                            const nFieldValidatorsReseted = {
                                ...fieldValidatorsToUnknown(
                                    fieldValidators,
                                    getFieldsToReset(props.resetOnChange, name)
                                ),
                                ...nFieldValidators,
                            }
                            const fieldsMerged = fieldsMerger(nFields, nFieldValidatorsReseted)

                            props.onChange &&
                                props.onChange(
                                    buildFormStatus(props.name, nFields, nFieldValidatorsReseted),
                                    fieldsMerged,
                                    name,
                                    props.unitsForm,
                                    subscriptions.updaterSubcrition.subject
                                )

                            runEmployees(
                                props.employees,
                                subscriptions.employeSubscriber,
                                [nField],
                                fieldsMerged,
                                props.unitsForm,
                                subscriptions.updaterSubcrition.subject,
                                'onChange',
                                employeesOnNextBuilded
                            )
                        }
                    ),
                })
            }}
            fields={fields}
            unitsForm={props.unitsForm}
            unitsHtml={props.unitsHtml}
            fieldsValidatorResult={fieldValidators}
            employeesStatus={employeesStatus}
            structure={props.structure}
            helper={props.helper}
            submit={props.submit}
            resource={props.resource}
        />
    )
}
